import { useEffect, useState } from 'react'
import { subscribe, syncNow, type SyncStatus } from '../net/syncEngine.js'
import { needsAttention, retry } from '../net/outbox.js'
import type { OutboxEntry } from '../net/localDb.js'

/**
 * Verbindungsanzeige – dauerhaft im Kopf der App, nie als Einblendung.
 *
 * Sie beantwortet drei Fragen, ohne dass jemand danach sucht: Bin ich
 * verbunden? Ist noch etwas unterwegs? Braucht etwas meine Aufmerksamkeit?
 *
 * Das ist zugleich die Gegenmassnahme gegen den heimlichen Datenspeicher:
 * ein Ausgangskorb, den niemand sieht, ist ein Rapport, den der Chef nie
 * bekommt und den der Monteur sicher erfasst zu haben glaubt.
 */

const STALE_NOTICE_MS = 24 * 3_600_000
const STALE_BLOCKING_MS = 72 * 3_600_000

export function describe(status: SyncStatus, oldestPendingAt: number | null): {
  tone: 'ok' | 'busy' | 'offline' | 'warn' | 'crit'
  text: string
  detail: string
} {
  const { counts } = status
  const problems = counts.failed + counts.conflict + counts.unclear

  if (problems > 0) {
    return {
      tone: 'crit',
      text: problems === 1 ? '1 Eintrag braucht dich' : `${problems} Einträge brauchen dich`,
      detail: 'Tippe darauf, um zu sehen, was fehlt.'
    }
  }
  if (!status.online) {
    return {
      tone: 'offline',
      text: counts.pending > 0 ? `Offline · ${counts.pending} wartet` : 'Offline',
      detail:
        counts.pending > 0
          ? 'Alles ist gespeichert und geht raus, sobald du wieder Empfang hast.'
          : 'Du kannst weiterarbeiten. Gespeichert wird lokal.'
    }
  }
  if (status.state === 'syncing' || counts.sending > 0) {
    return { tone: 'busy', text: 'Wird übertragen …', detail: '' }
  }
  if (counts.pending > 0) {
    const age = oldestPendingAt ? Date.now() - oldestPendingAt : 0
    if (age > STALE_BLOCKING_MS) {
      return {
        tone: 'crit',
        text: `${counts.pending} seit über 3 Tagen offen`,
        detail: 'Bitte einmal verbinden, damit nichts verlorengeht.'
      }
    }
    if (age > STALE_NOTICE_MS) {
      return {
        tone: 'warn',
        text: `${counts.pending} seit gestern offen`,
        detail: 'Wird beim nächsten Abgleich übertragen.'
      }
    }
    return { tone: 'busy', text: `${counts.pending} wartet`, detail: '' }
  }
  return {
    tone: 'ok',
    text: 'Aktuell',
    detail: status.lastSyncAt
      ? `Zuletzt abgeglichen ${new Date(status.lastSyncAt).toLocaleTimeString('de-CH', {
          hour: '2-digit',
          minute: '2-digit'
        })}`
      : ''
  }
}

export function ConnectionBar(): JSX.Element {
  const [status, setStatus] = useState<SyncStatus | null>(null)
  const [open, setOpen] = useState(false)
  const [attention, setAttention] = useState<OutboxEntry[]>([])
  const [oldest, setOldest] = useState<number | null>(null)

  useEffect(() => subscribe(setStatus), [])

  useEffect(() => {
    let cancelled = false
    const load = async (): Promise<void> => {
      const list = await needsAttention()
      if (cancelled) return
      setAttention(list)
      setOldest(list.length ? Math.min(...list.map((e) => e.createdAt)) : null)
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [status])

  if (!status) return <div className="connbar tone-ok" />

  const view = describe(status, oldest)
  const clickable = attention.length > 0

  return (
    <div className={`connbar tone-${view.tone}`}>
      <button
        type="button"
        className="connbar-main"
        onClick={() => (clickable ? setOpen((v) => !v) : void syncNow({ force: true }))}
        aria-expanded={clickable ? open : undefined}
      >
        <span className="connbar-dot" aria-hidden="true" />
        <span className="connbar-text">{view.text}</span>
        {view.detail && <span className="connbar-detail">{view.detail}</span>}
      </button>

      {open && attention.length > 0 && (
        <ul className="connbar-list">
          {attention.map((e) => (
            <li key={e.id}>
              <span className="connbar-type">{describeType(e.type)}</span>
              <span className="connbar-error">{e.lastError ?? 'Unbekannter Fehler'}</span>
              {e.status !== 'conflict' && (
                <button type="button" onClick={() => void retry(e.id).then(() => syncNow())}>
                  Nochmal versuchen
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Technische Typen in etwas übersetzen, das ein Handwerker liest. */
export function describeType(type: string): string {
  const map: Record<string, string> = {
    'customer.create': 'Neuer Kunde',
    'customer.update': 'Kunde geändert',
    'project.create': 'Neues Projekt',
    'catalog.item.save': 'Leistung gespeichert',
    /*
      Die Typen heissen so, wie net/rapports.ts sie erzeugt.

      Hier stand nur 'rapport.submit' – einen solchen Typ gibt es nicht. Ein
      hängengebliebener Rapport erschien deshalb als «rapport.create» in der
      Liste, also als technische Kennung statt als Wort.
    */
    'rapport.create': 'Rapport',
    'rapport.update': 'Rapport geändert',
    'rapport.submit': 'Rapport',
    'project.members.set': 'Zuweisung zum Projekt',
    'project.update': 'Projekt geändert',
    'document.cancel': 'Storno',
    'document.pdf': 'PDF erzeugen',
    'quoterequest.create': 'Offertanfrage',
    'document.finalize': 'Rechnung abschliessen',
    'document.send': 'Rechnung senden',
    'payment.record': 'Zahlung'
  }
  return map[type] ?? type
}
